import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Option } from "@/constants/common";
import { useDebounce } from "@/hooks/use-debounce";
import { useLocationsList } from "@/queries/use-locationList";
import { Location } from "@/services";
import { DEF_PAGINATION, DGT_Pagination } from "@/utils/utils";
import { getStoredRealm } from "@/utils/auth-storage";

/** Service Location is stored on the billing forms as a `{ uuid: name }` map. */
export type ServiceLocationMap = { [uuid: string]: string };

export interface ServiceLocationOptions {
  /** Current page of Locations, with the selected one kept resolvable. */
  options: Option[];
  /** Wire to the dropdown's `onSearch`; debounced and resets to page 0. */
  onSearch: (searchKey: string) => void;
  /** Builds the `{ uuid: name }` value the form stores from the picked uuid. */
  toServiceLocation: (uuid: string | null) => ServiceLocationMap | undefined;
  /** Pagination state for the infinite-scroll dropdown. */
  pagination: DGT_Pagination;
  setPagination: Dispatch<SetStateAction<DGT_Pagination>>;
  /** Total Locations across all pages. */
  totalElements?: number;
  loading: boolean;
}

/**
 * Options for an infinite-scroll Service Location dropdown.
 *
 * The Locations endpoint is paginated and search-driven, so the Location
 * already saved on a claim is usually outside the page currently loaded — the
 * dropdown then renders blank even though the form holds a value. The selected
 * `{ uuid: name }` is prepended when the loaded page does not carry it, so a
 * prefilled Location always renders; `CustomSelectScroll` accumulates the
 * remaining pages as the user scrolls.
 *
 * @param selected the form's current Service Location value
 */
export function useServiceLocationOptions(
  selected?: ServiceLocationMap
): ServiceLocationOptions {
  const [searchKey, setSearchKey] = useDebounce<string>("");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: locationList, isFetching } = useLocationsList({
    pagination,
    searchKey,
    status: true,
    providerGroupId: getStoredRealm() ?? "",
  });

  const fetchedOptions: Option[] = useMemo(
    () =>
      (locationList?.data?.content as Location[] | undefined)
        ?.filter((it) => !!it.uuid)
        .map((it) => ({ value: it.uuid as string, label: it.name })) ?? [],
    [locationList?.data?.content]
  );

  const [selectedUuid, selectedName] = Object.entries(selected ?? {})[0] ?? [];

  const options = useMemo(
    () =>
      selectedUuid && !fetchedOptions.some((it) => it.value === selectedUuid)
        ? [
            { value: selectedUuid, label: selectedName ?? "" },
            ...fetchedOptions,
          ]
        : fetchedOptions,
    [fetchedOptions, selectedUuid, selectedName]
  );

  const onSearch = useCallback(
    (key: string) => {
      setSearchKey(key);
      setPagination((prev) => ({ ...prev, page: 0 }));
    },
    [setSearchKey]
  );

  const toServiceLocation = useCallback(
    (uuid: string | null) => {
      const option = options.find((it) => it.value === uuid);
      return option ? { [option.value]: option.label } : undefined;
    },
    [options]
  );

  return {
    options,
    onSearch,
    toServiceLocation,
    pagination,
    setPagination,
    totalElements: locationList?.data?.page?.totalElements,
    loading: isFetching,
  };
}
